import { useState, useRef, useCallback, useEffect, Fragment } from "react";

// ─── Color palette ──────────────────────────────────────────────────────────
// 16 maximum-contrast colors (a standard "distinct colors" set), ordered so
// that NEIGHBOURING labels are opposites on the color wheel. Pink is left out:
// it is reserved for phrases.
//   border = strong color (underline / outline)
//   dot    = darker shade for text and dots so it stays readable
//   alpha  = fill strength (pale colors get a stronger fill, dark ones a lighter one)
// The first 8 labels use a solid underline, the last 8 a dashed one, so two
// colors that still look alike never share the same line style.
const hexToRgba = (hex, alpha) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};
const COLOR_PALETTE = [
  ["#E6194B", "#A80F34", 0.32], // red
  ["#42D4F4", "#0A8CA8", 0.45], // cyan
  ["#911EB4", "#6A1285", 0.28], // purple
  ["#BFEF45", "#6E9100", 0.55], // lime
  ["#000075", "#000075", 0.2], // navy
  ["#F58231", "#B85A0E", 0.38], // orange
  ["#469990", "#2B6B65", 0.34], // teal
  ["#DCBEFF", "#7A55B8", 0.6], // lavender
  ["#000000", "#000000", 0.16], // black
  ["#FFE119", "#9A8200", 0.55], // yellow
  ["#4363D8", "#2A429E", 0.3], // blue
  ["#9A6324", "#6B4316", 0.32], // brown
  ["#3CB44B", "#228230", 0.32], // green
  ["#A9A9A9", "#666666", 0.42], // gray
  ["#800000", "#800000", 0.22], // maroon
  ["#808000", "#5A5A00", 0.3], // olive
].map(([border, dot, alpha], i) => ({
  bg: hexToRgba(border, alpha),
  border,
  dot,
  line: i < 8 ? "2px solid" : "3px dashed",
}));

const DEFAULT_LABELS = [
  "Important",
  "Question",
  "Agree",
  "Disagree",
  "Revisit",
  "Key Insight",
];

const buildLabelColors = (labels) =>
  labels.map((label, i) =>
    label === PHRASE_LABEL
      ? { name: label, ...PHRASE_COLOR }
      : { name: label, ...COLOR_PALETTE[i % COLOR_PALETTE.length] },
  );

// Old saved phrases (a separate "kind") become normal "Phrase" annotations;
// only one phrase is kept; hosts without a "Phrase" label drop them.
const normalizeAnnotations = (anns, labels) => {
  const idx = labels.indexOf(PHRASE_LABEL);
  const out = (anns || [])
    .map((a) => {
      if (!isPhrase(a)) return a;
      if (idx < 0) return null;
      const { kind, primary, ...rest } = a;
      return { ...rest, label: PHRASE_LABEL, colorIdx: idx };
    })
    .filter(Boolean);
  const last = [...out].reverse().find(isPhrase);
  return out.filter((a) => !isPhrase(a) || a === last);
};

const generateId = () => Math.random().toString(36).substr(2, 9);

// ─── Phrase ──────────────────────────────────────────────────────────────────
// "Phrase" is just another label in the field list (hosts add it to `labels`).
// It is drawn with its own style (bold, pink top line, [ ] brackets) and only
// ONE phrase is kept per text. Fields inside it are grouped under it in the
// sidebar.
const PHRASE_LABEL = "Phrase";
const PHRASE_COLOR = {
  name: "Phrase",
  bg: "rgba(236, 72, 153, 0.12)",
  border: "#EC4899",
  dot: "#BE185D",
};
const isPhrase = (a) => a?.label === PHRASE_LABEL || a?.kind === "phrase";
// Manual annotations (fields that are not in the text) have no span.
const inSpan = (a, p) =>
  a.start != null && a.start >= p.start && a.end <= p.end;

// Special values offered at the bottom of the Value dropdown.
// `key` is what the annotation stores; `out` is what is written to the table.
// Default and Unaffected both write an empty string, so the stored marker key
// is what tells "explicitly empty" apart from "nothing chosen".
const SPECIAL_VALUES = [
  { key: "__unknown__", label: "Unknown (---)", out: "---" },
  { key: "__nodirect__", label: "No direction (_)", out: "_" },
  { key: "__default__", label: "Default (empty)", out: "" },
  { key: "__unaffected__", label: "Unaffected (empty)", out: "" },
];
const specialOf = (v) => SPECIAL_VALUES.find((s) => s.key === v) || null;
// An annotation whose value is written even when it is empty
const isExplicit = (a) =>
  !!specialOf(a.value) ||
  (a.label === SIGN_LABEL && !(a.value || "").trim());

// Sign has a fixed list of five choices; the host's fieldOptions.Sign is ignored.
// Special keys write empty, --- or _ ; "negative" / "positive" are written as is.
const SIGN_LABEL = "Sign";
const SIGN_CHOICES = [
  { key: "__unaffected__", label: "unaffected" }, // writes empty
  { key: "__unknown__", label: "unknown" }, // writes ---
  { key: "__nodirect__", label: "noDirection" }, // writes _
  { key: "positive", label: "positive" },
  { key: "negative", label: "negative" },
];

// ─── Mechanism taxonomy: category › subcategory › mechanism ─────────────────
// A line without "- " is a category. "- Sub: a, b, c" is a subcategory with its
// mechanisms; "- a, b, c" (no "Sub:") lists mechanisms directly under the
// category. The picked mechanism's name is what is written to the field.
const MECH_LABEL = "Mechanism";
const MECHANISM_TAXONOMY_TEXT = `Protein post-translational modifications
- Phosphate-related: phosphorylation, serine phosphorylation, threonine phosphorylation, tyrosine phosphorylation, histidine phosphorylation, aspartate phosphorylation, dephosphorylation
- Acetylation/acylation: acetylation, deacetylation, succinylation, desuccinylation, malonylation, demalonylation, propionylation, depropionylation, butyrylation, debutyrylation, crotonylation, decrotonylation, lactylation, delactylation, glutarylation, benzoylation, formylation, other lysine acylations
- Methylation: methylation, monomethylation, dimethylation, trimethylation, lysine methylation, arginine methylation, demethylation
- Ubiquitin and ubiquitin-like: ubiquitination, ubiquitylation, monoubiquitination, multi-monoubiquitination, polyubiquitination, K48-linked ubiquitination, K63-linked ubiquitination, other chain topologies, deubiquitination, SUMOylation, deSUMOylation, NEDDylation, deNEDDylation, ISGylation, deISGylation, UFMylation, deUFMylation, FAT10 conjugation, other ubiquitin-like conjugation
- Glycosylation: glycosylation, N-linked glycosylation, O-linked glycosylation, C-linked glycosylation, O-GlcNAcylation, O-GalNAcylation, glycosaminoglycan attachment, deglycosylation, de-O-GlcNAcylation, glycan trimming, glycan extension/remodeling, glycation
- Lipidation: palmitoylation, S-acylation, depalmitoylation, myristoylation, prenylation, farnesylation, geranylgeranylation, GPI anchoring, cholesterylation, phosphatidylethanolamine conjugation, other fatty-acylation/lipid attachment
- ADP-ribose and nucleotide-based: ADP-ribosylation, mono-ADP-ribosylation, poly-ADP-ribosylation, PARylation, de-ADP-ribosylation, AMPylation, adenylylation, deAMPylation, UMPylation, uridylylation, GMPylation, phosphoribosylation
- Oxidative/redox: S-nitrosylation, denitrosylation, S-glutathionylation, deglutathionylation, sulfenylation, sulfinylation, sulfonylation, cysteine oxidation, cysteine reduction, methionine oxidation, methionine reduction, protein carbonylation, tyrosine nitration, disulfide-bond formation, disulfide-bond reduction
- Hydroxylation: hydroxylation, prolyl hydroxylation, lysyl hydroxylation, asparaginyl hydroxylation, tyrosine hydroxylation
- Other covalent PTMs: sulfation, desulfation, citrullination, deimination, γ-carboxylation, amidation, deamidation, biotinylation, lipoylation, phosphopantetheinylation, iodination, halogenation, glutamylation, deglutamylation, glycylation, deglycylation, transglutamination, covalent cross-linking, isopeptide-bond formation, pyroglutamate formation
Protein processing and maturation
- proteolytic cleavage, limited proteolysis, precursor cleavage, proprotein processing, signal-peptide cleavage, transit-peptide cleavage, initiator methionine removal, N-terminal processing, C-terminal processing, ectodomain shedding, zymogen activation, protein fragmentation, protein splicing, intein excision, disulfide-bond rearrangement, protein folding, protein refolding, chaperone-mediated folding, conformational maturation
Molecular binding and complex formation
- Generic: binding, association, dissociation, unbinding, complex formation, complex dissociation, recruitment, docking, sequestration, release
- By partner: protein-protein binding, protein-peptide binding, protein-DNA binding, protein-RNA binding, DNA-DNA interaction, RNA-RNA interaction, DNA-RNA interaction, protein-lipid binding, protein-carbohydrate binding, protein-small-molecule binding, metabolite binding, ligand-receptor binding, antibody-antigen binding, enzyme-substrate binding, cofactor binding, metal-ion binding, nucleotide binding, ATP binding, GTP binding
- Complex architecture: dimerization, homodimerization, heterodimerization, trimerization, tetramerization, oligomerization, polymerization, depolymerization, multimerization, complex assembly, complex disassembly
Conformational and state changes
- conformational change, conformational activation, conformational inactivation, folding, unfolding, refolding, domain rearrangement, exposure of binding site, masking of binding site, opening, closing, channel opening, channel closing, receptor conformational change, cis-trans isomerization, proline isomerization
- Nucleotide-dependent switches: GDP binding, GDP-GTP exchange, nucleotide exchange, GTP hydrolysis, ATP hydrolysis, ADP-ATP exchange
Enzymatic biochemical reactions
- Oxidation/reduction: oxidation, reduction, electron transfer, hydrogen transfer, dehydrogenation, hydrogenation, oxygenation, monooxygenation, dioxygenation, peroxide reduction
- Group transfer: phosphoryl transfer, methyl transfer, acyl transfer, acetyl transfer, glycosyl transfer, amino-group transfer, transamination, alkyl transfer, sulfur-group transfer, one-carbon-group transfer, nucleotidyl transfer
- Hydrolysis: hydrolysis, ester hydrolysis, phosphoester hydrolysis, glycosidic-bond hydrolysis, peptide-bond hydrolysis, amide hydrolysis, lipid hydrolysis
- Lyase-type: bond cleavage without hydrolysis, elimination, addition, decarboxylation, dehydration, hydration, deamination, aldol cleavage
- Isomerization: isomerization, racemization, epimerization, intramolecular group transfer, mutase reaction
- Ligation: ligation, condensation, ATP-dependent bond formation, C-C bond formation, C-O bond formation, C-N bond formation, C-S bond formation
Metabolic transformations
- biosynthesis, synthesis, catabolism, degradation, conversion, cleavage, carboxylation, amination, acylation, deacylation, esterification, de-esterification, cyclization, ring opening, conjugation, deconjugation
Transport, translocation, and localization
- Generic: translocation, localization, relocalization, redistribution, import, export, uptake, efflux, secretion
- Membrane transport: transmembrane transport, passive diffusion, facilitated diffusion, active transport, primary active transport, secondary active transport, channel-mediated transport, carrier-mediated transport, pump-mediated transport, symport, antiport, exchange transport
- Ion movement: ion transport, proton transport, sodium transport, potassium transport, calcium transport, chloride transport, ion influx, ion efflux
- Protein trafficking: nuclear import, nuclear export, nuclear translocation, membrane recruitment, membrane insertion, cytoplasm-to-membrane translocation, ER import, ER export, Golgi transport, mitochondrial import, mitochondrial export, peroxisomal import, lysosomal targeting
- Vesicle-mediated: vesicle formation, vesicular transport, vesicle docking, vesicle tethering, vesicle fusion, endocytosis, receptor-mediated endocytosis, phagocytosis, pinocytosis, macropinocytosis, exocytosis, endosomal trafficking, recycling, retrograde transport, anterograde transport
DNA-related processes
- DNA replication, DNA synthesis, replication initiation, replication elongation, replication termination, transcription-factor binding, promoter binding, enhancer binding, repressor binding
- DNA modification: DNA methylation, DNA demethylation, cytosine hydroxymethylation, further oxidized cytosine modifications, adenine methylation
- Chromatin: nucleosome assembly, nucleosome disassembly, chromatin remodeling, chromatin opening, chromatin compaction, heterochromatin formation, euchromatin formation, histone acetylation, histone deacetylation, histone methylation, histone demethylation, histone phosphorylation, histone ubiquitination
- Repair/recombination: DNA cleavage, DNA ligation, DNA repair, base excision, nucleotide excision, mismatch repair, double-strand-break repair, homologous recombination, nonhomologous end joining, DNA recombination, strand exchange
Transcription
- transcription, transcription initiation, transcription elongation, transcription termination, promoter recruitment, RNA polymerase recruitment, transcription-complex assembly, transcriptional activation, transcriptional repression, transcriptional enhancement, transcriptional silencing
RNA processing and regulation
- Maturation: RNA processing, RNA splicing, alternative splicing, intron removal, exon inclusion, exon skipping, RNA cleavage, RNA ligation, 5′ capping, decapping, polyadenylation, deadenylation, uridylation, de-uridylation
- Chemical modification: RNA methylation, m6A methylation, m5C methylation, other base/ribose methylation, RNA demethylation, pseudouridylation, thiolation, RNA acetylation
- Editing: RNA editing, A→I editing, C→U editing, U→C editing
- Localization/transport: nuclear RNA export, nuclear RNA import, RNA localization, mRNA transport
- Stability: RNA stabilization, RNA destabilization, RNA degradation, mRNA decay, nonsense-mediated decay, exonuclease-mediated degradation, endonucleolytic cleavage
- Small-RNA regulation: miRNA binding, miRNA-mediated repression, RNA interference, siRNA-mediated silencing, RISC recruitment, mRNA cleavage, translational repression
Translation
- translation, translation initiation, translation elongation, translation termination, ribosome recruitment, ribosome assembly, ribosome dissociation, translational activation, translation inhibition, translation enhancement, tRNA aminoacylation, codon recognition, peptide-bond formation, polypeptide elongation, cotranslational folding, cotranslational translocation, cotranslational modification
Protein turnover and degradation
- protein degradation, proteolysis, protease-mediated cleavage, ubiquitin-dependent degradation, ubiquitin-independent degradation, proteasomal degradation, lysosomal degradation, endosomal degradation, autophagic degradation, chaperone-mediated autophagy, ER-associated degradation, ERAD, mitochondrial protein degradation, protein destabilization, protein stabilization, half-life increase, half-life decrease
Receptor and signaling mechanisms
- Receptor events: ligand binding, receptor binding, receptor dimerization, receptor oligomerization, receptor phosphorylation, receptor autophosphorylation, receptor internalization, receptor recycling, receptor degradation, receptor shedding
- G-protein signaling: G-protein binding, GDP release, GTP loading, guanine-nucleotide exchange, G-protein activation, G-protein inactivation, subunit dissociation, subunit reassociation
- Second messengers: second-messenger synthesis, second-messenger degradation, cAMP production, cAMP degradation, cGMP production, IP3 production, DAG production, calcium release, calcium influx, calcium efflux
- Ion channels: channel binding, channel gating, channel activation, channel inhibition, ion permeation
Enzyme regulation
- substrate binding, product binding, inhibitor binding, activator binding, allosteric binding, allosteric regulation, competitive inhibition, noncompetitive inhibition, uncompetitive inhibition, mixed inhibition, product inhibition, feedback inhibition, feed-forward activation, covalent modification, proteolytic activation, proteolytic inactivation, subunit association, subunit dissociation, cofactor insertion, cofactor removal, localization-dependent regulation
Membrane, lipid, and organelle dynamics
- Membrane: membrane association, membrane dissociation, membrane fusion, membrane fission, budding, tubulation, curvature generation, pore formation, lipid flip-flop, lipid scrambling
- Organelle: organelle biogenesis, organelle fusion, organelle fission, mitochondrial fusion, mitochondrial fission, ER-organelle contact formation, organelle tethering, organelle inheritance, mitophagy, pexophagy
- Lipid signaling: lipid synthesis, lipid degradation, phosphoinositide phosphorylation, phosphoinositide dephosphorylation, phospholipid cleavage, eicosanoid synthesis, sterol transport
Cytoskeletal, motor, and mechanical
- actin polymerization, actin depolymerization, microtubule polymerization, microtubule depolymerization, filament nucleation, branching, bundling, cross-linking, severing, capping, uncapping, motor-protein stepping, ATP-dependent movement, cargo transport, contractility, traction-force generation, mechanotransduction, adhesion assembly, adhesion disassembly, cell migration
Quality control, stress, and damage responses
- protein quality control, misfolded-protein recognition, chaperone recruitment, unfolded protein response, heat-shock response, oxidative-stress response, DNA-damage signaling, ER stress, proteotoxic stress, aggregate formation, aggregate clearance, autophagy, selective autophagy, apoptotic signaling, necrotic signaling
Intercellular and extracellular
- cell-cell adhesion, cell-matrix adhesion, extracellular-matrix assembly, matrix degradation, proteoglycan remodeling, ligand secretion, paracrine signaling, autocrine signaling, endocrine signaling, juxtacrine signaling, synaptic transmission, gap-junction communication, extracellular-vesicle release, extracellular-vesicle uptake`;

const parseTaxonomy = (txt) => {
  const groups = [];
  let cat = "";
  txt.split("\n").forEach((raw) => {
    const line = raw.trim();
    if (!line) return;
    if (!line.startsWith("- ")) {
      cat = line;
      return;
    }
    let body = line.slice(2);
    let sub = null;
    const ci = body.indexOf(":");
    if (ci > 0 && !body.slice(0, ci).includes(",")) {
      sub = body.slice(0, ci).trim();
      body = body.slice(ci + 1);
    }
    const items = body
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);
    groups.push({ cat, sub, items });
  });
  return groups;
};
const MECH_GROUPS = parseTaxonomy(MECHANISM_TAXONOMY_TEXT);

// The value an annotation writes into its table field: a special value,
// the preset/typed value, or (if none) the highlighted text.
const valueOf = (a) => {
  const s = specialOf(a.value);
  if (s) return s.out;
  const v = (a.value || "").trim();
  // Sign has no "use highlighted text": nothing chosen = unaffected = ""
  if (a.label === SIGN_LABEL && !v) return "";
  // a plain "unknown" option from the host's list also means "---"
  if (v.toLowerCase() === "unknown") return "---";
  return v || (a.text || "").trim();
};

// ─── Star rating icons ───────────────────────────────────────────────────────
function StarIcon({ filled, hovered, size = 22 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={filled ? "#FFB81C" : hovered ? "rgba(255,184,28,0.3)" : "none"}
      stroke={filled ? "#FFB81C" : hovered ? "#FFB81C" : "#94908a"}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ transition: "all 0.15s ease" }}
    >
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

// ─────────────────────────────────────────────
// STYLES
// ─────────────────────────────────────────────

const styles = `
@import url('https://fonts.googleapis.com/css2?family=Newsreader:ital,opsz,wght@0,6..72,300;0,6..72,400;0,6..72,500;1,6..72,400&family=DM+Sans:wght@400;500;600&display=swap');

.ta-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  z-index: 9998;
  animation: ta-fadeIn 0.2s ease;
}

.ta-modal {
  position: fixed;
  inset: 20vh;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 32px 80px rgba(0,0,0,0.25), 0 0 0 1px rgba(0,0,0,0.06);
  animation: ta-slideUp 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes ta-fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes ta-slideUp {
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
}

.ta-scroll::-webkit-scrollbar { width: 5px; }
.ta-scroll::-webkit-scrollbar-track { background: transparent; }
.ta-scroll::-webkit-scrollbar-thumb { background: #D5D0C8; border-radius: 10px; }

.ta-sidebar-card {
  border-left: 3px solid transparent;
}

.ta-sidebar-card:hover {
  background: rgba(0,0,0,0.02);
}

.ta-btn {
  transition: all 0.15s ease;
  font-family: 'DM Sans', sans-serif;
}

.ta-btn:hover {
  background: rgba(0,0,0,0.06);
}

.ta-fade-in {
  animation: ta-fadeIn 0.3s ease;
}

.ta-general-note-textarea:focus {
  border-color: #FFB81C !important;
  box-shadow: 0 0 0 2px rgba(255,184,28,0.15) !important;
}

.ta-tab-btn {
  transition: all 0.15s ease;
  cursor: pointer;
  border: none;
  font-family: 'DM Sans', sans-serif;
}

.ta-tab-btn:hover {
  opacity: 1 !important;
}

.ta-info-panel {
  transition: max-height 0.3s ease, padding 0.3s ease, opacity 0.3s ease;
  overflow: hidden;
}

.ta-info-panel.ta-info-expanded {
  max-height: 600px;
  opacity: 1;
}

.ta-info-panel.ta-info-collapsed {
  max-height: 0;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  opacity: 0;
}

.ta-info-toggle {
  transition: all 0.15s ease;
  font-family: 'DM Sans', sans-serif;
}

/* Phrase brackets — pseudo-elements, so they are not text nodes */
.ta-phrase-start::before,
.ta-phrase-end::after {
  color: #BE185D;
  font-weight: 800;
  font-size: 1.25em;
  line-height: 1;
}
.ta-phrase-start::before { content: "["; margin-right: 3px; }
.ta-phrase-end::after { content: "]"; margin-left: 3px; }

/* "+ Add field" button in the sidebar */
.ta-add-field-btn:hover {
  background: rgba(255,255,255,0.14) !important;
}
`;

// Marker shown before a label name: a round dot for fields, "[ ]" for Phrase
function LabelMarker({ c, size = 8, ring = false }) {
  if (c.name === PHRASE_LABEL)
    return (
      <span
        style={{
          color: PHRASE_COLOR.dot,
          fontWeight: 800,
          fontSize: size + 4,
          lineHeight: 1,
          letterSpacing: "-1px",
          flexShrink: 0,
        }}
      >
        [ ]
      </span>
    );
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: c.dot,
        flexShrink: 0,
        boxShadow: ring ? `0 0 0 2px ${c.bg}` : "none",
      }}
    />
  );
}

// ─── MechanismPicker: searchable category › subcategory dropdown ─────────────
function MechanismPicker({ value, onChange, compact = false, allowText = true }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const fs = compact ? 11 : 12;

  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (
        panelRef.current?.contains(e.target) ||
        btnRef.current?.contains(e.target)
      )
        return;
      setOpen(false);
    };
    document.addEventListener("mousedown", h);
    panelRef.current?.scrollIntoView({ block: "nearest" });
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const query = q.trim().toLowerCase();
  const visible = MECH_GROUPS.map((g) => {
    const groupHit =
      query &&
      (g.cat.toLowerCase().includes(query) ||
        (g.sub || "").toLowerCase().includes(query));
    const items =
      !query || groupHit
        ? g.items
        : g.items.filter((it) => it.toLowerCase().includes(query));
    return { ...g, items };
  }).filter((g) => g.items.length);
  const total = visible.reduce((n, g) => n + g.items.length, 0);

  const pick = (v) => {
    onChange(v);
    setOpen(false);
    setQ("");
  };

  let lastCat = null;
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <button
        ref={btnRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        title={value || ""}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 6,
          padding: compact ? "2px 6px" : "4px 8px",
          fontSize: fs,
          border: "1px solid #E0DCD4",
          borderRadius: 6,
          background: "#FDFCFA",
          color: value ? "#333" : "#999",
          cursor: "pointer",
          fontFamily: "'DM Sans',sans-serif",
          textAlign: "left",
        }}
      >
        <span style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
          {value || (allowText ? "Use highlighted text" : "Select a mechanism…")}
        </span>
        <span style={{ fontSize: 8, opacity: 0.6 }}>▼</span>
      </button>

      {open && (
        <div
          ref={panelRef}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            top: "100%",
            marginTop: 4,
            background: "white",
            border: "1px solid #EDE9E1",
            borderRadius: 10,
            boxShadow:
              "0 8px 28px rgba(0,0,0,0.14), 0 2px 6px rgba(0,0,0,0.06)",
            zIndex: 400,
            fontFamily: "'DM Sans',sans-serif",
            overflow: "hidden",
          }}
        >
          <div style={{ padding: 6, borderBottom: "1px solid #F0ECE5" }}>
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  e.stopPropagation();
                  e.nativeEvent.stopImmediatePropagation();
                  setOpen(false);
                } else if (e.key === "Enter" && visible[0]) {
                  e.preventDefault();
                  pick(visible[0].items[0]);
                }
              }}
              placeholder="Search mechanism, category or subcategory…"
              style={{
                width: "100%",
                boxSizing: "border-box",
                padding: "5px 8px",
                fontSize: 12,
                border: "1px solid #E0DCD4",
                borderRadius: 6,
                outline: "none",
                background: "#FDFCFA",
                fontFamily: "'DM Sans',sans-serif",
              }}
            />
            <div style={{ fontSize: 10, color: "#AAA", marginTop: 4 }}>
              {total} mechanism{total === 1 ? "" : "s"}
              {query ? " match" : ""}
            </div>
          </div>

          <div
            className="ta-scroll"
            onWheel={(e) => e.stopPropagation()}
            style={{
              maxHeight: 240,
              overflowY: "auto",
              overscrollBehavior: "contain",
              padding: 4,
            }}
          >
            {(allowText || value) && !query && (
              <button
                type="button"
                className="ta-btn"
                onClick={() => pick("")}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  padding: "5px 10px",
                  border: "none",
                  background: "transparent",
                  borderRadius: 6,
                  fontSize: 12,
                  fontStyle: "italic",
                  color: "#888",
                  cursor: "pointer",
                }}
              >
                {allowText ? "Use highlighted text" : "Clear selection"}
              </button>
            )}

            {total === 0 && (
              <div
                style={{
                  padding: "14px 10px",
                  fontSize: 12,
                  color: "#AAA",
                  textAlign: "center",
                }}
              >
                No mechanism matches “{q}”.
              </div>
            )}

            {visible.map((g, gi) => {
              const showCat = g.cat !== lastCat;
              lastCat = g.cat;
              return (
                <Fragment key={gi}>
                  {showCat && (
                    <div
                      style={{
                        position: "sticky",
                        top: 0,
                        background: "#F4F1EA",
                        padding: "4px 10px",
                        marginTop: 4,
                        borderRadius: 5,
                        fontSize: 10,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        color: "#5A5A55",
                        zIndex: 1,
                      }}
                    >
                      {g.cat}
                    </div>
                  )}
                  {g.sub && (
                    <div
                      style={{
                        padding: "4px 10px 2px 16px",
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#003594",
                      }}
                    >
                      {g.sub}
                    </div>
                  )}
                  {g.items.map((it) => {
                    const selected = it === value;
                    return (
                      <button
                        key={it}
                        type="button"
                        className="ta-btn"
                        onClick={() => pick(it)}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          width: "100%",
                          textAlign: "left",
                          padding: g.sub ? "4px 10px 4px 26px" : "4px 10px 4px 16px",
                          border: "none",
                          background: selected
                            ? "rgba(0,53,148,0.08)"
                            : "transparent",
                          borderRadius: 6,
                          fontSize: 12,
                          fontWeight: selected ? 600 : 400,
                          color: selected ? "#003594" : "#333",
                          cursor: "pointer",
                        }}
                      >
                        {it}
                        {selected && <span>✓</span>}
                      </button>
                    );
                  })}
                </Fragment>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── ValuePicker: choose a preset value instead of the highlighted text ──────
// Shown for labels that have a fixed list of allowed values (Sign, Connection
// Type, ...). "Use highlighted text" keeps the old behaviour. For fields added
// manually there is no highlighted text, so `allowText={false}` changes the
// empty option to "Select a value…". A "Special" group at the bottom offers
// Unknown (---), No direct (_), Default (empty) and Unaffected (empty).
function ValuePicker({
  options,
  value,
  onChange,
  field,
  compact = false,
  allowText = true,
}) {
  const isSign = field === SIGN_LABEL;
  const isMech = field === MECH_LABEL;
  if (!isSign && !isMech && !options?.length) return null;
  // Sign has exactly five choices; saved "unknown" / "---" show as the --- choice
  const shown =
    isSign && ["unknown", "---"].includes(String(value || "").trim().toLowerCase())
      ? "__unknown__"
      : value || (isSign ? "__unaffected__" : "");
  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: 6,
        marginTop: compact ? 5 : 0,
        marginBottom: compact ? 0 : 10,
        fontFamily: "'DM Sans',sans-serif",
      }}
    >
      <span
        style={{
          fontSize: compact ? 10 : 11,
          fontWeight: 600,
          color: "#999",
        }}
      >
        Value
      </span>
      {isMech ? (
        <MechanismPicker
          value={value}
          onChange={onChange}
          compact={compact}
          allowText={allowText}
        />
      ) : (
      <select
        value={shown}
        onChange={(e) => onChange(e.target.value)}
        style={{
          flex: 1,
          minWidth: 0,
          padding: compact ? "2px 4px" : "4px 6px",
          fontSize: compact ? 11 : 12,
          border: "1px solid #E0DCD4",
          borderRadius: 6,
          background: "#FDFCFA",
          color: "#333",
          cursor: "pointer",
        }}
      >
        {!isSign && (
          <option value="">
            {allowText ? "Use highlighted text" : "Select a value…"}
          </option>
        )}
        {isSign ? (
          SIGN_CHOICES.map((c) => (
            <option key={c.key} value={c.key}>
              {c.label}
            </option>
          ))
        ) : (
          <>
            {options.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
            <optgroup label="Special">
              {SPECIAL_VALUES.filter(
                (sp) =>
                  sp.key !== "__unknown__" ||
                  !options.some((o) => String(o).toLowerCase() === "unknown"),
              ).map((sp) => (
                <option key={sp.key} value={sp.key}>
                  {sp.label}
                </option>
              ))}
            </optgroup>
          </>
        )}
      </select>
      )}
    </div>
  );
}

// ─── LabelDropdown component ─────────────────────────────────────────────────
function LabelDropdown({ colors, currentIdx, onChange, compact = false }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selectedRef = useRef(null);
  const current = colors[currentIdx];

  // When the list opens, bring the currently chosen label into view
  useEffect(() => {
    if (open && selectedRef.current)
      selectedRef.current.scrollIntoView({ block: "nearest" });
  }, [open]);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} style={{ position: "relative", zIndex: open ? 300 : 1 }}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setOpen(!open);
        }}
        className="ta-btn"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          background: current.bg,
          padding: compact ? "2px 8px" : "3px 10px",
          borderRadius: current.name === PHRASE_LABEL ? 6 : 12,
          fontSize: compact ? 10 : 11,
          fontWeight: current.name === PHRASE_LABEL ? 800 : 500,
          color: current.dot,
          border:
            current.name === PHRASE_LABEL
              ? `1.5px dashed ${PHRASE_COLOR.border}`
              : "none",
          cursor: "pointer",
          fontFamily: "'DM Sans',sans-serif",
          whiteSpace: "nowrap",
        }}
      >
        <LabelMarker c={current} size={compact ? 5 : 6} />
        {current.name}
        <span
          style={{
            fontSize: 8,
            marginLeft: 2,
            opacity: 0.7,
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
          }}
        >
          ▼
        </span>
      </button>

      {open && (
        <div
          className="ta-scroll"
          onWheel={(e) => e.stopPropagation()}
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            marginTop: 4,
            background: "white",
            borderRadius: 10,
            boxShadow:
              "0 8px 28px rgba(0,0,0,0.12), 0 2px 6px rgba(0,0,0,0.06)",
            border: "1px solid #EDE9E1",
            padding: 4,
            minWidth: 170,
            // scrollable list instead of a tall menu that runs off the screen
            maxHeight: "min(260px, 40vh)",
            overflowY: "auto",
            overscrollBehavior: "contain",
            zIndex: 300,
          }}
        >
          {colors.map((c, idx) => {
            const isPh = c.name === PHRASE_LABEL;
            const selected = idx === currentIdx;
            return (
              <Fragment key={c.name}>
                {/* Phrase is set apart from the plain fields by a divider */}
                {isPh && (
                  <div
                    style={{
                      borderTop: `1px dashed ${PHRASE_COLOR.border}`,
                      margin: "5px 6px 4px",
                      opacity: 0.6,
                    }}
                  />
                )}
                <button
                  ref={selected ? selectedRef : null}
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange(idx);
                    setOpen(false);
                  }}
                  className="ta-btn"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    width: "100%",
                    padding: "6px 10px",
                    borderRadius: isPh ? 5 : 7,
                    border: isPh
                      ? `1px dashed ${PHRASE_COLOR.border}`
                      : "none",
                    background: selected
                      ? isPh
                        ? PHRASE_COLOR.bg
                        : "rgba(0,0,0,0.04)"
                      : isPh
                        ? "rgba(236,72,153,0.05)"
                        : "transparent",
                    cursor: "pointer",
                    fontSize: 12,
                    color: isPh
                      ? PHRASE_COLOR.dot
                      : selected
                        ? c.dot
                        : "#666",
                    fontWeight: isPh ? 800 : selected ? 600 : 400,
                    fontFamily: "'DM Sans',sans-serif",
                    textAlign: "left",
                  }}
                >
                  <LabelMarker c={c} ring={selected} />
                  {c.name}
                  {isPh && !selected && (
                    <span
                      style={{
                        marginLeft: "auto",
                        fontSize: 9,
                        fontWeight: 500,
                        fontStyle: "italic",
                        opacity: 0.75,
                      }}
                    >
                      one per text
                    </span>
                  )}
                  {selected && (
                    <span style={{ marginLeft: "auto", fontSize: 11 }}>✓</span>
                  )}
                </button>
              </Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── StarRating component ────────────────────────────────────────────────────
function StarRating({ value = 0, onChange, size = 22 }) {
  const [hoverVal, setHoverVal] = useState(0);

  return (
    <div
      style={{ display: "inline-flex", gap: 2 }}
      onMouseLeave={() => setHoverVal(0)}
    >
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          onClick={() => onChange(star === value ? 0 : star)}
          onMouseEnter={() => setHoverVal(star)}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: 2,
            display: "flex",
            alignItems: "center",
            transform: hoverVal === star ? "scale(1.2)" : "scale(1)",
            transition: "transform 0.15s ease",
          }}
        >
          <StarIcon
            filled={star <= value}
            hovered={star <= hoverVal && star > value}
            size={size}
          />
        </button>
      ))}
    </div>
  );
}

/*
 * ════════════════════════════════════════════════════════════════════════════
 *  TextAnnotatorModal  (named export)
 * ════════════════════════════════════════════════════════════════════════════
 */

export function TextAnnotatorModal({
  isOpen,
  onClose,
  text = "",
  labels = DEFAULT_LABELS,
  initialAnnotations = [],
  initialGeneralNote = "",
  initialEvaluation = null,
  rowData: rowDataProp = null, // renamed: the live version is built below
  onSave,
  // true  → highlighted text is written into the row's fields (default; Statements)
  // false → annotations are only recorded (general text such as a Phrase)
  applyToFields = true,
  title = "",
  // { "Sign": ["positive", ...], ... } – labels that offer a fixed value list.
  // Picking one writes that value into the field instead of the highlighted text.
  fieldOptions = {},
}) {
  const ANNOTATION_COLORS = buildLabelColors(labels);
  const colorOf = (a) =>
    isPhrase(a) ? PHRASE_COLOR : ANNOTATION_COLORS[a.colorIdx];

  const [annotations, setAnnotations] = useState(() =>
    normalizeAnnotations(initialAnnotations, labels),
  );
  const [activeColorIdx, setActiveColorIdx] = useState(0);
  const lastSelectRef = useRef(0);
  // Field labels that were annotated when the modal opened. If one of them is
  // no longer annotated (deleted / re-labelled), its table value is cleared.
  const initialLabelsRef = useRef(null);
  if (initialLabelsRef.current === null) {
    initialLabelsRef.current = new Set(
      normalizeAnnotations(initialAnnotations, labels).map((a) => a.label),
    );
  }
  const [selectedAnnotation, setSelectedAnnotation] = useState(null);
  const [noteInput, setNoteInput] = useState("");
  const [isEditingNote, setIsEditingNote] = useState(false);
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });
  const [generalNote, setGeneralNote] = useState(initialGeneralNote);
  const EVAL_FIELDS = [
    "Regulator Name",
    "Regulator Type",
    "Regulator Subtype",
    "Regulator Compartment",
    "Regulated Name",
    "Regulated Type",
    "Regulated Subtype",
    "Regulated Compartment",
    "Sign",
    "Connection Type",
    "Mechanism",
    "Site",
    "Cell Line",
    "Cell Type",
    "Tissue Type",
    "Organism",
    "Score",
    "Source",
  ];

  const buildDefaultEvaluation = () => ({
    overallRating: 0,
    evaluationNote: "",
    fields: Object.fromEntries(
      EVAL_FIELDS.map((f) => [f, { rating: 0, note: "" }]),
    ),
  });

  const [evaluation, setEvaluation] = useState(() =>
    initialEvaluation
      ? {
          ...buildDefaultEvaluation(),
          ...initialEvaluation,
          fields: {
            ...buildDefaultEvaluation().fields,
            ...(initialEvaluation.fields || {}),
          },
        }
      : buildDefaultEvaluation(),
  );
  const [sidebarTab, setSidebarTab] = useState("annotations");
  const [infoPanelOpen, setInfoPanelOpen] = useState(true);
  // "+ Add field" chooser (fields that are not in the text)
  const [addOpen, setAddOpen] = useState(false);
  const [focusId, setFocusId] = useState(null);

  const textRef = useRef(null);
  const popoverRef = useRef(null);
  const modalRef = useRef(null);
  function expandConnectionType(val) {
    if (!val) return val;
    const map = { I: "Indirect", D: "Direct", i: "Indirect", d: "Direct" };
    return map[val.trim()] ? `${map[val.trim()]} (${val.trim()})` : val;
  }
  const changeAnnotationLabel = useCallback(
    (annId, newColorIdx) => {
      const newLabel = labels[newColorIdx] || labels[0];
      setAnnotations((prev) => {
        const target = prev.find((a) => a.id === annId);
        // a manual field has no text, so it can't become the Phrase
        if (target?.manual && newLabel === PHRASE_LABEL) return prev;
        return (
          prev
            .map((a) =>
              a.id === annId
                ? { ...a, colorIdx: newColorIdx, label: newLabel, value: "" }
                : a,
            )
            // only ONE phrase: choosing "Phrase" removes any other phrase
            .filter(
              (a) =>
                newLabel !== PHRASE_LABEL || a.id === annId || !isPhrase(a),
            )
        );
      });
    },
    [labels],
  );

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setAnnotations(normalizeAnnotations(initialAnnotations, labels));
      initialLabelsRef.current = new Set(
        normalizeAnnotations(initialAnnotations, labels).map((a) => a.label),
      );
      setSelectedAnnotation(null);
      setIsEditingNote(false);
      setActiveColorIdx(0);
      setGeneralNote(initialGeneralNote);
      setEvaluation(
        initialEvaluation
          ? {
              ...buildDefaultEvaluation(),
              ...initialEvaluation,
              fields: {
                ...buildDefaultEvaluation().fields,
                ...(initialEvaluation.fields || {}),
              },
            }
          : buildDefaultEvaluation(),
      );
      setSidebarTab("annotations");
      setInfoPanelOpen(true);
      setAddOpen(false);
    }
  }, [isOpen, initialAnnotations, initialGeneralNote, initialEvaluation]);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [isOpen]);

  // Escape to close
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  // ── Selection handler ────────────────────────────────────────────────────
  const handleTextSelect = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed || !textRef.current) return;

    const range = selection.getRangeAt(0);
    const container = textRef.current;
    if (
      !container.contains(range.startContainer) ||
      !container.contains(range.endContainer)
    )
      return;

    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    let charCount = 0,
      startOffset = 0,
      endOffset = 0,
      foundStart = false,
      foundEnd = false;

    while (walker.nextNode()) {
      const node = walker.currentNode;
      if (node === range.startContainer) {
        startOffset = charCount + range.startOffset;
        foundStart = true;
      }
      if (node === range.endContainer) {
        endOffset = charCount + range.endOffset;
        foundEnd = true;
      }
      if (foundStart && foundEnd) break;
      charCount += node.textContent.length;
    }

    if (!foundStart || !foundEnd || startOffset === endOffset) return;
    const selected = text.slice(startOffset, endOffset);
    if (!selected.trim()) return;

    const rect = range.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    const ann = {
      id: generateId(),
      start: startOffset,
      end: endOffset,
      text: selected,
      note: "",
      value: "", // optional preset value (e.g. Sign → "positive")
      label: labels[activeColorIdx] || labels[0],
      colorIdx: activeColorIdx,
      draft: true, // only kept if the popover's Save is clicked
      timestamp: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };

    lastSelectRef.current = Date.now();
    setAnnotations((prev) => [...prev, ann]);
    setSelectedAnnotation(ann.id);
    setNoteInput("");
    setIsEditingNote(true);
    setPopoverPos({
      top: rect.bottom - containerRect.top + 8,
      left: rect.left - containerRect.left + rect.width / 2,
    });
    selection.removeAllRanges();
    setSidebarTab("annotations");
  }, [text, activeColorIdx, labels]);

  // ── CRUD helpers ─────────────────────────────────────────────────────────
  // Save (popover button / Ctrl+Enter): commit the highlight and its note.
  const saveNote = () => {
    if (selectedAnnotation) {
      setAnnotations((prev) => {
        const next = prev.map((a) =>
          a.id === selectedAnnotation
            ? { ...a, note: noteInput, draft: false }
            : a,
        );
        const me = next.find((a) => a.id === selectedAnnotation);
        // only ONE phrase: committing a phrase removes any other phrase
        return me && isPhrase(me)
          ? next.filter((a) => a.id === me.id || !isPhrase(a))
          : next;
      });
    }
    setIsEditingNote(false);
    setSelectedAnnotation(null); // close the popover once it is saved
  };

  // Cancel / click anywhere else: throw the draft away, ignore note edits.
  const discardEdit = () => {
    const a = annotations.find((x) => x.id === selectedAnnotation);
    if (a?.draft) setAnnotations((prev) => prev.filter((x) => x.id !== a.id));
    setSelectedAnnotation(null);
    setIsEditingNote(false);
  };

  const deleteAnnotation = (id) => {
    setAnnotations((prev) => prev.filter((a) => a.id !== id));
    if (selectedAnnotation === id) {
      setSelectedAnnotation(null);
      setIsEditingNote(false);
    }
  };

  // Choose a preset value for an annotation ("" = use the highlighted text)
  const setAnnotationValue = (id, value) =>
    setAnnotations((prev) =>
      prev.map((a) => (a.id === id ? { ...a, value } : a)),
    );

  const setAnnotationNote = (id, note) =>
    setAnnotations((prev) => prev.map((a) => (a.id === id ? { ...a, note } : a)));

  // Add a field that is NOT in the text (no highlight; the value is typed or
  // picked in the sidebar card). It starts with the row's current value, if any.
  const addManualField = (idx) => {
    const id = generateId();
    const label = labels[idx];
    setAnnotations((prev) => [
      ...prev,
      {
        id,
        start: null,
        end: null,
        text: "",
        note: "",
        value: String(rowDataProp?.[label] ?? ""),
        label,
        colorIdx: idx,
        manual: true,
        draft: false,
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      },
    ]);
    setFocusId(id);
    setAddOpen(false);
  };

  const handleAnnotationClick = (id, e) => {
    e.stopPropagation();
    if (Date.now() - lastSelectRef.current < 300) return;
    const ann = annotations.find((a) => a.id === id);
    if (!ann) return;
    setSelectedAnnotation(id);
    setNoteInput(ann.note);
    setIsEditingNote(false);
    const rect = e.target.getBoundingClientRect();
    const containerRect = textRef.current.getBoundingClientRect();
    setPopoverPos({
      top: rect.bottom - containerRect.top + 8,
      left: rect.left - containerRect.left + rect.width / 2,
    });
  };

  // Close popover on outside click
  useEffect(() => {
    const handler = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        discardEdit(); // clicking elsewhere never saves
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  });

  const updateEvaluation = (key, value) => {
    setEvaluation((prev) => ({ ...prev, [key]: value }));
  };

  const updateFieldEvaluation = (field, key, value) => {
    setEvaluation((prev) => ({
      ...prev,
      fields: {
        ...prev.fields,
        [field]: { ...prev.fields[field], [key]: value },
      },
    }));
  };
  // ── Render highlighted text ──────────────────────────────────────────────
  const renderAnnotatedText = () => {
    if (!annotations.length) return <span>{text}</span>;

    const charColors = new Array(text.length).fill(null);
    const charAnns = new Array(text.length).fill(null);
    const charPhrase = new Array(text.length).fill(null);
    [...annotations]
      .sort((a, b) => (a.start ?? 0) - (b.start ?? 0))
      .forEach((ann) => {
        if (ann.manual) return; // not in the text → nothing to highlight
        for (let i = ann.start; i < Math.min(ann.end, text.length); i++) {
          if (isPhrase(ann)) {
            charPhrase[i] = ann.id;
          } else {
            charColors[i] = ann.colorIdx;
            charAnns[i] = ann.id;
          }
        }
      });

    const segs = [];
    let i = 0;
    while (i < text.length) {
      const col = charColors[i],
        aid = charAnns[i],
        pid = charPhrase[i];
      let j = i + 1;
      while (
        j < text.length &&
        charColors[j] === col &&
        charAnns[j] === aid &&
        charPhrase[j] === pid
      )
        j++;
      const seg = text.slice(i, j);
      // Phrase layer — deliberately unlike the field highlights (which are
      // filled + underlined): bold text, a thick pink top line and [ ] brackets
      // drawn with CSS (::before/::after), so they never change text offsets.
      const phraseStyle =
        pid !== null
          ? {
              borderTop: `3px solid ${PHRASE_COLOR.border}`,
              fontWeight: 600,
              ...(pid === selectedAnnotation
                ? { boxShadow: `0 -2px 0 0 ${PHRASE_COLOR.border}` }
                : {}),
            }
          : {};
      const phraseClass =
        pid !== null
          ? `ta-phrase${i === 0 || charPhrase[i - 1] !== pid ? " ta-phrase-start" : ""}${j >= text.length || charPhrase[j] !== pid ? " ta-phrase-end" : ""}`
          : undefined;
      if (col !== null) {
        const c = ANNOTATION_COLORS[col];
        const sel = aid === selectedAnnotation;
        segs.push(
          <mark
            key={`${i}-${j}`}
            className={phraseClass}
            onClick={(e) => handleAnnotationClick(aid, e)}
            style={{
              backgroundColor: c.bg,
              borderBottom: `${c.line || "2px solid"} ${c.border}`,
              padding: "1px 0",
              borderRadius: 2,
              cursor: "pointer",
              transition: "all 0.2s",
              boxShadow: sel ? `0 0 0 2px ${c.border}` : "none",
              filter: sel ? "brightness(0.92)" : "none",
              ...phraseStyle,
              ...(sel ? { boxShadow: `0 0 0 2px ${c.border}` } : {}),
              ...(annotations.some((x) => x.id === aid && x.draft)
                ? { outline: `2px dashed ${c.border}`, outlineOffset: 1 }
                : {}),
            }}
          >
            {seg}
          </mark>,
        );
      } else if (pid !== null) {
        segs.push(
          <span
            key={`${i}-${j}`}
            className={phraseClass}
            onClick={(e) => handleAnnotationClick(pid, e)}
            style={{
              padding: "1px 0",
              cursor: "pointer",
              ...phraseStyle,
            }}
          >
            {seg}
          </span>,
        );
      } else {
        segs.push(<span key={`${i}-${j}`}>{seg}</span>);
      }
      i = j;
    }
    return segs;
  };

  const activeAnn = annotations.find((a) => a.id === selectedAnnotation);
  const committed = annotations.filter((a) => !a.draft);

  // ── Annotation → field sync ──────────────────────────────────────────────
  // Each annotation's label is a row field name. The field's value is a
  // special value (---, _, or an explicit empty), the preset/typed value
  // (Value picker or manual entry), or (if none) the highlighted text.
  // If several share a label, the most recent one wins.
  // Every annotation's label is a row field name (including "Phrase" when the
  // host has such a column).
  const annotatedFields = {};
  committed.forEach((a) => {
    const v = valueOf(a);
    // an explicit special value (even an empty one) is written to the field
    if (a.label && (v || isExplicit(a))) annotatedFields[a.label] = v;
  });
  // A field that was annotated before but no longer is → empty it. The empty
  // value shows in the Row Information panel now and is written to the table
  // on Save & Close.
  const currentLabels = new Set(
    committed
      .filter((a) => valueOf(a) || isExplicit(a))
      .map((a) => a.label),
  );
  initialLabelsRef.current.forEach((l) => {
    if (l && !currentLabels.has(l)) annotatedFields[l] = "";
  });

  // Row data with the annotated values applied. Everything below that reads
  // `rowData` (Row Information panel, Evaluation tab) now updates live.
  const rowData = rowDataProp
    ? applyToFields
      ? { ...rowDataProp, ...annotatedFields }
      : rowDataProp
    : null;

  // ── Sidebar cards (fields and phrases) ───────────────────────────────────
  const renderAnnCard = (ann) => {
    const phrase = isPhrase(ann);
    const color = colorOf(ann);
    const isActive = ann.id === selectedAnnotation;
    return (
      <div
        key={ann.id}
        className="ta-sidebar-card"
        onClick={() => {
          if (ann.manual) return; // no text to point at, so no popover
          setSelectedAnnotation(ann.id);
          setNoteInput(ann.note);
          setIsEditingNote(false);
        }}
        style={{
          padding: "10px 12px",
          borderRadius: 10,
          background: phrase ? "#FFF5FA" : "white",
          cursor: ann.manual ? "default" : "pointer",
          border: `${isActive ? "8px" : "1px"} solid ${isActive ? color.border : phrase ? "#F5C6DA" : "#EDEBE6"}`,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 5,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <LabelDropdown
              colors={ANNOTATION_COLORS}
              currentIdx={ann.colorIdx}
              onChange={(newIdx) => changeAnnotationLabel(ann.id, newIdx)}
              compact
            />
            <span style={{ fontSize: 10, color: "#BBB" }}>{ann.timestamp}</span>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              deleteAnnotation(ann.id);
            }}
            style={{
              background: "none",
              border: "none",
              color: "#000",
              cursor: "pointer",
              fontSize: 14,
              padding: 0,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        {ann.manual ? (
          <div onClick={(e) => e.stopPropagation()}>
            {fieldOptions[ann.label]?.length ||
            ann.label === SIGN_LABEL ||
            ann.label === MECH_LABEL ? (
              <ValuePicker
                compact
                allowText={false}
                field={ann.label}
                options={fieldOptions[ann.label]}
                value={ann.value}
                onChange={(v) => setAnnotationValue(ann.id, v)}
              />
            ) : (
              <input
                autoFocus={ann.id === focusId}
                value={ann.value}
                onChange={(e) => setAnnotationValue(ann.id, e.target.value)}
                placeholder={`Type ${ann.label}…`}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  padding: "4px 6px",
                  fontSize: 12,
                  border: "1px solid #E0DCD4",
                  borderRadius: 6,
                  background: "#FDFCFA",
                  fontFamily: "'DM Sans',sans-serif",
                  outline: "none",
                }}
              />
            )}
            <input
              value={ann.note}
              onChange={(e) => setAnnotationNote(ann.id, e.target.value)}
              placeholder="Note (optional)…"
              style={{
                width: "100%",
                boxSizing: "border-box",
                marginTop: 5,
                padding: "3px 6px",
                fontSize: 11,
                border: "none",
                borderTop: "1px solid #F0ECE5",
                background: "transparent",
                color: "#666",
                fontFamily: "'DM Sans',sans-serif",
                outline: "none",
              }}
            />
            <div
              style={{
                fontSize: 9,
                color: "#BBB",
                marginTop: 3,
                fontStyle: "italic",
              }}
            >
              Added manually, not in the text
            </div>
          </div>
        ) : (
          <>
            <p
              style={{
                fontSize: 12,
                fontStyle: "italic",
                color: "#888",
                margin: "0 0 3px",
                lineHeight: 1.4,
                fontFamily: "'Newsreader',serif",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              "{ann.text.length > 50 ? ann.text.slice(0, 50) + "…" : ann.text}"
            </p>
            <ValuePicker
              compact
              field={ann.label}
              options={fieldOptions[ann.label]}
              value={ann.value}
              onChange={(v) => setAnnotationValue(ann.id, v)}
            />
            {ann.note && (
              <p
                style={{
                  fontSize: 11,
                  color: "#666",
                  margin: 0,
                  lineHeight: 1.5,
                  paddingTop: 3,
                  borderTop: "1px solid #F0ECE5",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {ann.note}
              </p>
            )}
          </>
        )}
      </div>
    );
  };

  // Without phrases: flat list (newest first). With phrases: each phrase shows
  // the field annotations that sit inside it. Manual fields have no position
  // in the text, so they go under "Other fields".
  const renderAnnotationList = () => {
    const phrases = committed
      .filter(isPhrase)
      .sort((a, b) => a.start - b.start);
    const fields = committed.filter((a) => !isPhrase(a));
    if (phrases.length === 0)
      return [...fields].reverse().map((a) => renderAnnCard(a));

    const ownerOf = (a) =>
      phrases
        .filter((p) => inSpan(a, p))
        .sort((x, y) => x.end - x.start - (y.end - y.start))[0] || null;
    const loose = fields.filter((a) => !ownerOf(a));

    return (
      <>
        {phrases.map((p) => {
          const items = fields.filter((a) => ownerOf(a)?.id === p.id);
          return (
            <div
              key={p.id}
              style={{ display: "flex", flexDirection: "column", gap: 6 }}
            >
              {renderAnnCard(p)}
              <div
                style={{
                  marginLeft: 10,
                  paddingLeft: 8,
                  borderLeft: `2px solid ${PHRASE_COLOR.border}`,
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                }}
              >
                {items.length === 0 ? (
                  <p
                    style={{
                      fontSize: 11,
                      color: "rgba(255,255,255,0.7)",
                      fontStyle: "italic",
                      margin: "2px 0",
                    }}
                  >
                    Highlight fields inside this phrase to group them here.
                  </p>
                ) : (
                  items.map((a) => renderAnnCard(a))
                )}
              </div>
            </div>
          );
        })}
        {loose.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                color: "rgba(255,255,255,0.6)",
                marginTop: 4,
              }}
            >
              Other fields
            </div>
            {loose.map((a) => renderAnnCard(a))}
          </div>
        )}
      </>
    );
  };

  if (!isOpen) return null;

  const tabs = [
    { key: "annotations", icon: "✎", label: "Annotations" },
    { key: "note", icon: "📝", label: "General Note" },
    { key: "evaluation", icon: "★", label: "Evaluation" },
  ];

  return (
    <>
      <style>{styles}</style>

      <div className="ta-backdrop" onClick={onClose} />

      <div className="ta-modal" ref={modalRef}>
        {/* ── Header ── */}
        <header
          style={{
            padding: "14px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #00205B",
            background: "#00205B",
            fontFamily: "'DM Sans',sans-serif",
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {title && (
              <span
                style={{
                  color: "#fff",
                  fontSize: 12,
                  fontWeight: 600,
                  letterSpacing: "0.04em",
                  fontFamily: "'DM Sans',sans-serif",
                }}
              >
                {title}
              </span>
            )}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {onSave && (
              <button
                className="ta-btn"
                onClick={() => {
                  onSave({
                    // empty manual cards write nothing, so they are not kept
                    annotations: committed.filter(
                      (a) => !a.manual || (a.value || "").trim() || isExplicit(a),
                    ),
                    generalNote,
                    evaluation,
                    fieldUpdates: applyToFields ? annotatedFields : {},
                  });
                  onClose?.();
                }}
                style={{
                  padding: "6px 14px",
                  borderRadius: 8,
                  border: "none",
                  background: "#00AD6E",
                  color: "white",
                  fontSize: 12,
                  fontWeight: 500,
                  cursor: "pointer",
                  margin: "0 20px",
                }}
              >
                Save & Close
              </button>
            )}

            <button
              onClick={onClose}
              style={{
                width: 30,
                height: 30,
                borderRadius: 8,
                border: "1px solid #E0DCD4",
                background: "#fff",
                color: "#000",
                fontSize: 24,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                lineHeight: 1,
                fontFamily: "'DM Sans',sans-serif",
              }}
            >
              ×
            </button>
          </div>
        </header>

        {/* ── Body ── */}
        <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
          {/* Left column: scrollable text + Row Information below it */}
          <div
            style={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Text panel (scrollable) */}
            <div
              className="ta-scroll"
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                padding: "40px 56px",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <div
                style={{ maxWidth: 660, width: "100%", position: "relative" }}
              >
                <span
                  style={{
                    fontSize: 10,
                    fontFamily: "'DM Sans',sans-serif",
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    color: "#B0A898",
                    marginBottom: 14,
                    padding: "8px",
                    borderRadius: "8px",
                    fontWeight: "bold",
                  }}
                >
                  {labels.includes(PHRASE_LABEL)
                    ? "Select text, then pick its field (or Phrase)"
                    : "Select any text to annotate"}
                </span>

                <div
                  ref={textRef}
                  onMouseUp={handleTextSelect}
                  style={{
                    fontSize: 18,
                    lineHeight: 2,
                    letterSpacing: "0.01em",
                    color: "#333",
                    whiteSpace: "pre-wrap",
                    position: "relative",
                    cursor: "text",
                    userSelect: "text",
                    fontFamily: "'Newsreader','Georgia',serif",
                    padding: "16px 8px 40px 8px",
                  }}
                >
                  {renderAnnotatedText()}

                  {/* ── Popover (never for manual cards) ── */}
                  {selectedAnnotation && activeAnn && !activeAnn.manual && (
                    <div
                      ref={popoverRef}
                      style={{
                        position: "absolute",
                        top: popoverPos.top,
                        left: Math.max(140, Math.min(popoverPos.left, 520)),
                        transform: "translateX(-50%)",
                        background: "white",
                        borderRadius: 12,
                        boxShadow:
                          "0 8px 32px rgba(0,0,0,0.12),0 2px 8px rgba(0,0,0,0.06)",
                        padding: 16,
                        width: 280,
                        zIndex: 100,
                        fontFamily: "'DM Sans',sans-serif",
                        border: "1px solid #EDE9E1",
                      }}
                    >
                      {/* Arrow */}
                      <div
                        style={{
                          position: "absolute",
                          top: -6,
                          left: "50%",
                          transform: "translateX(-50%) rotate(45deg)",
                          width: 12,
                          height: 12,
                          background: "white",
                          borderTop: "1px solid #EDE9E1",
                          borderLeft: "1px solid #EDE9E1",
                        }}
                      />

                      <div
                        style={{
                          fontSize: 12,
                          color: "#999",
                          marginBottom: 8,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <LabelDropdown
                          colors={ANNOTATION_COLORS}
                          currentIdx={activeAnn.colorIdx}
                          onChange={(newIdx) =>
                            changeAnnotationLabel(activeAnn.id, newIdx)
                          }
                        />
                      </div>

                      <div
                        style={{
                          fontSize: 13,
                          fontStyle: "italic",
                          color: "#777",
                          marginBottom: 10,
                          padding: "6px 8px",
                          background: "#FAF9F6",
                          borderRadius: 6,
                          borderLeft: `3px solid ${colorOf(activeAnn)?.border || "#ccc"}`,
                          maxHeight: 60,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          fontFamily: "'Newsreader',serif",
                        }}
                      >
                        "
                        {activeAnn.text.length > 80
                          ? activeAnn.text.slice(0, 80) + "…"
                          : activeAnn.text}
                        "
                      </div>

                      {/* Preset value (Sign, Connection Type, ...) */}
                      <ValuePicker
                        field={activeAnn.label}
                        options={fieldOptions[activeAnn.label]}
                        value={activeAnn.value}
                        onChange={(v) => setAnnotationValue(activeAnn.id, v)}
                      />

                      {isEditingNote ? (
                        <div>
                          {activeAnn.draft && (
                            <div
                              style={{
                                fontSize: 11,
                                color: "#B87333",
                                marginBottom: 6,
                              }}
                            >
                              Not saved yet — click Save to keep this highlight.
                            </div>
                          )}
                          <textarea
                            autoFocus
                            value={noteInput}
                            onChange={(e) => setNoteInput(e.target.value)}
                            placeholder="Add a note…"
                            style={{
                              width: "100%",
                              minHeight: 60,
                              padding: 8,
                              fontSize: 13,
                              border: "1px solid #E0DCD4",
                              borderRadius: 8,
                              resize: "vertical",
                              fontFamily: "'DM Sans',sans-serif",
                              color: "#333",
                              background: "#FDFCFA",
                              outline: "none",
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && (e.metaKey || e.ctrlKey))
                                saveNote();
                            }}
                          />
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "flex-end",
                              gap: 6,
                              marginTop: 8,
                            }}
                          >
                            <button
                              onClick={discardEdit}
                              className="ta-btn"
                              style={{
                                padding: "5px 12px",
                                borderRadius: 6,
                                border: "1px solid #E0DCD4",
                                background: "white",
                                fontSize: 12,
                                cursor: "pointer",
                                color: "#888",
                              }}
                            >
                              Cancel
                            </button>
                            <button
                              onClick={saveNote}
                              className="ta-btn"
                              style={{
                                padding: "5px 14px",
                                borderRadius: 6,
                                border: "none",
                                background: colorOf(activeAnn)?.dot || "#555",
                                color: "white",
                                fontSize: 12,
                                fontWeight: 500,
                                cursor: "pointer",
                              }}
                            >
                              Save ⌘↵
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          {activeAnn.note ? (
                            <p
                              style={{
                                fontSize: 13,
                                color: "#555",
                                margin: 0,
                                lineHeight: 1.5,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {activeAnn.note}
                            </p>
                          ) : (
                            <p
                              style={{
                                fontSize: 13,
                                color: "#BBB",
                                margin: 0,
                                fontStyle: "italic",
                              }}
                            >
                              No note yet
                            </p>
                          )}
                          <button
                            onClick={() => {
                              setNoteInput(activeAnn.note);
                              setIsEditingNote(true);
                            }}
                            className="ta-btn"
                            style={{
                              marginTop: 8,
                              padding: "4px 10px",
                              borderRadius: 6,
                              border: "1px solid #E0DCD4",
                              background: "transparent",
                              fontSize: 11,
                              color: "#999",
                              cursor: "pointer",
                            }}
                          >
                            {activeAnn.note ? "Edit" : "Add note"}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ── Row Info Panel (minimizable) — sits below the text, not over it ── */}
            {rowData && (
              <div
                style={{
                  flexShrink: 0,
                  maxHeight: "50%",
                  display: "flex",
                  flexDirection: "column",
                  fontFamily: "'DM Sans',sans-serif",
                  background: "#FAFBFC",
                  borderTop: "1px solid #C8C9C7",
                }}
              >
                {/* Toggle bar — always visible */}
                <button
                  className="ta-info-toggle"
                  onClick={() => setInfoPanelOpen((prev) => !prev)}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "8px 24px",
                    background: "#FFB81C",
                    border: "none",
                    borderBottom: infoPanelOpen ? "1px solid #E0E0E0" : "none",
                    cursor: "pointer",
                    fontSize: 14,
                    fontWeight: 600,
                    color: "#09205b",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                  }}
                >
                  <span
                    style={{ display: "flex", alignItems: "center", gap: 6 }}
                  >
                    Row Information
                  </span>
                  <span
                    style={{
                      fontSize: 14,
                      transform: infoPanelOpen
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                      transition: "transform 0.25s ease",
                      display: "inline-block",
                    }}
                  >
                    ▲
                  </span>
                </button>

                {/* Collapsible content */}
                <div
                  className={`ta-info-panel ${infoPanelOpen ? "ta-info-expanded" : "ta-info-collapsed"}`}
                  style={{
                    fontSize: 11,
                    color: "#333",
                    lineHeight: 1.5,
                    display: "flex",
                    flexDirection: "row",
                    flex: "1 1 auto",
                    minHeight: 0,
                    overflowX: "auto",
                    overflowY: "auto",
                    background: "#f0f2f5",
                  }}
                >
                  {/* SECTION 1: Interaction */}
                  <div
                    style={{
                      flex: 2,
                      minWidth: 0,
                      borderRight: "2px solid #B0B5BF",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <div
                      style={{
                        background: "#003594",
                        color: "#fff",
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        padding: "4px 16px",
                        borderBottom: "1px solid #002478",
                      }}
                    >
                      Interaction
                    </div>
                    <div style={{ padding: "6px 16px 8px" }}>
                      {/* Arrow line */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                          marginBottom: 4,
                          fontWeight: 700,
                          fontSize: 12,
                        }}
                      >
                        <span style={{ color: "#003594" }}>
                          {rowData["Regulator Name"] || "—"}
                        </span>
                        {(() => {
                          // → only for positive, ⊣ only for negative;
                          // unknown (---), no direct (_), empty, etc. stay neutral
                          const sign = String(rowData["Sign"] ?? "")
                            .trim()
                            .toLowerCase();
                          const arrow =
                            sign === "positive"
                              ? { sym: "→", color: "#00AD6E" }
                              : sign === "negative"
                                ? { sym: "⊣", color: "#FF5B45" }
                                : sign === "---" || sign === "unknown"
                                  ? { sym: "---", color: "#8A8F99" }
                                  : sign === ""
                                    ? { sym: "", color: "#8A8F99" } // unaffected / no sign
                                    : { sym: "—", color: "#8A8F99" }; // noDirection (_)
                          return (
                            <span style={{ color: arrow.color, fontSize: 14 }}>
                              {arrow.sym}
                            </span>
                          );
                        })()}
                        <span style={{ color: "#003594" }}>
                          {rowData["Regulated Name"] || "—"}
                        </span>
                      </div>

                      <div style={{ display: "flex", gap: 0 }}>
                        {[
                          { label: "Regulator", prefix: "Regulator" },
                          { label: "Regulated", prefix: "Regulated" },
                        ].map(({ label, prefix }) => (
                          <div
                            key={label}
                            style={{
                              flex: 1,
                              minWidth: 0,
                              borderRight: "1px solid #D0D3D9",
                              paddingRight: 8,
                              marginRight: 8,
                            }}
                          >
                            <span style={{ fontWeight: 600, color: "#003594" }}>
                              {label}:{" "}
                            </span>
                            <span style={{ fontWeight: 600 }}>
                              {rowData[`${prefix} Name`] || "—"}
                            </span>
                            {[
                              ["Type", rowData[`${prefix} Type`]],
                              ["Subtype", rowData[`${prefix} Subtype`]],
                              ["HGNC", rowData[`${prefix} HGNC Symbol`]],
                              [
                                "ID",
                                [
                                  rowData[`${prefix} Database`],
                                  rowData[`${prefix} ID`],
                                ]
                                  .filter(Boolean)
                                  .join(" | ") || null,
                              ],
                              [
                                "Compartment",
                                [
                                  rowData[`${prefix} Compartment`],
                                  rowData[`${prefix} Compartment ID`],
                                ]
                                  .filter(Boolean)
                                  .join(" | ") || null,
                              ],
                            ].map(([lbl, val]) => (
                              <div
                                key={lbl}
                                style={{
                                  paddingLeft: 8,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                <span
                                  style={{ fontWeight: 500, color: "#666" }}
                                >
                                  {lbl}:{" "}
                                </span>
                                <span
                                  style={{
                                    fontStyle: val ? "normal" : "italic",
                                    color: val ? "#000" : "#bbb",
                                  }}
                                >
                                  {val || "—"}
                                </span>
                              </div>
                            ))}
                          </div>
                        ))}

                        {/* Connection / Mechanism / Site */}
                        <div style={{ flex: 1, minWidth: 0, paddingLeft: 8 }}>
                          {[
                            [
                              "Conn. Type",
                              expandConnectionType(rowData["Connection Type"]),
                            ],
                            ["Mechanism", rowData["Mechanism"]],
                            ["Site", rowData["Site"]],
                          ].map(([lbl, val]) => (
                            <div
                              key={lbl}
                              style={{
                                whiteSpace: "normal",
                                wordBreak: "break-word",
                              }}
                            >
                              <span style={{ fontWeight: 500, color: "#666" }}>
                                {lbl}:{" "}
                              </span>
                              <span
                                style={{
                                  color: val ? "#000" : "#bbb",
                                  fontStyle: val ? "normal" : "italic",
                                }}
                              >
                                {val || "—"}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* SECTION 2: Context */}
                  <div
                    style={{
                      flexShrink: 0,
                      borderRight: "2px solid #B0B5BF",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <div
                      style={{
                        background: "#003594",
                        color: "#fff",
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        padding: "4px 16px",
                        borderBottom: "1px solid #002478",
                      }}
                    >
                      Context
                    </div>
                    <div style={{ padding: "6px 16px 8px" }}>
                      {[
                        ["Cell Line", rowData["Cell Line"]],
                        ["Cell Type", rowData["Cell Type"]],
                        ["Tissue", rowData["Tissue Type"]],
                        ["Organism", rowData["Organism"]],
                      ].map(([lbl, val]) => (
                        <div key={lbl} style={{ whiteSpace: "nowrap" }}>
                          <span style={{ fontWeight: 500, color: "#666" }}>
                            {lbl}:{" "}
                          </span>
                          <span
                            style={{
                              color: val ? "#000" : "#bbb",
                              fontStyle: val ? "normal" : "italic",
                            }}
                          >
                            {val || "—"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* SECTION 3: Provenance */}
                  <div
                    style={{
                      flexShrink: 0,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <div
                      style={{
                        background: "#003594",
                        color: "#fff",
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        padding: "4px 16px",
                        borderBottom: "1px solid #002478",
                      }}
                    >
                      Provenance
                    </div>
                    <div style={{ padding: "6px 16px 8px" }}>
                      {[
                        ["Score", rowData["Score"]],
                        ["Source", rowData["Source"]],
                        ["Paper ID", rowData["Paper IDs"]],
                      ].map(([lbl, val]) => (
                        <div key={lbl} style={{ whiteSpace: "nowrap" }}>
                          <span style={{ fontWeight: 500, color: "#666" }}>
                            {lbl}:{" "}
                          </span>
                          <span
                            style={{
                              color: val ? "#000" : "#bbb",
                              fontStyle: val ? "normal" : "italic",
                            }}
                          >
                            {val || "—"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── Sidebar ── */}
          <aside
            className="ta-scroll ta-fade-in"
            style={{
              width: 300,
              borderLeft: "1px solid #00205B",
              background: "#003594",
              overflow: "auto",
              fontFamily: "'DM Sans',sans-serif",
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                display: "flex",
                borderBottom: "1px solid rgba(255,255,255,0.12)",
                flexShrink: 0,
              }}
            >
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  className="ta-tab-btn"
                  onClick={() => setSidebarTab(tab.key)}
                  style={{
                    flex: 1,
                    padding: "12px 6px 10px",
                    background:
                      sidebarTab === tab.key
                        ? "rgba(255,255,255,0.1)"
                        : "transparent",
                    color: "#fff",
                    fontSize: 11,
                    fontWeight: sidebarTab === tab.key ? 600 : 400,
                    opacity: sidebarTab === tab.key ? 1 : 0.6,
                    borderBottom:
                      sidebarTab === tab.key
                        ? "2px solid #FFB81C"
                        : "2px solid transparent",
                    textAlign: "center",
                    lineHeight: 1.3,
                  }}
                >
                  <span
                    style={{ fontSize: 14, display: "block", marginBottom: 2 }}
                  >
                    {tab.icon}
                  </span>
                  {tab.label}
                </button>
              ))}
            </div>

            <div style={{ flex: 1, overflow: "auto", padding: "18px 14px" }}>
              {/* ════ ANNOTATIONS TAB ════ */}
              {sidebarTab === "annotations" && (
                <>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: 18,
                    }}
                  >
                    <h3
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        color: "#fff",
                        margin: 0,
                      }}
                    >
                      Annotations
                    </h3>
                    <span
                      style={{
                        fontSize: 11,
                        color: "#000",
                        background: "#FFB81C",
                        padding: "2px 8px",
                        borderRadius: 10,
                        fontWeight: 500,
                      }}
                    >
                      {committed.length}
                    </span>
                  </div>

                  {/* ── + Add field (fields that are not in the text) ── */}
                  <button
                    onClick={() => setAddOpen((o) => !o)}
                    className="ta-btn ta-add-field-btn"
                    style={{
                      width: "100%",
                      marginBottom: 12,
                      padding: "8px",
                      borderRadius: 8,
                      border: "1px dashed rgba(255,255,255,0.5)",
                      background: addOpen
                        ? "rgba(255,255,255,0.12)"
                        : "transparent",
                      color: "#fff",
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    {addOpen ? "× Close" : "+ Add field (not in text)"}
                  </button>

                  {addOpen && (
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 6,
                        marginBottom: 14,
                        padding: 10,
                        borderRadius: 10,
                        background: "rgba(255,255,255,0.08)",
                      }}
                    >
                      {(() => {
                        const used = new Set(committed.map((a) => a.label));
                        const free = ANNOTATION_COLORS.map((c, i) => ({
                          c,
                          i,
                        })).filter(
                          ({ c }) =>
                            c.name !== PHRASE_LABEL && !used.has(c.name),
                        );
                        if (!free.length)
                          return (
                            <span
                              style={{
                                fontSize: 11,
                                color: "rgba(255,255,255,0.7)",
                              }}
                            >
                              All fields are already added.
                            </span>
                          );
                        return free.map(({ c, i }) => (
                          <button
                            key={c.name}
                            onClick={() => addManualField(i)}
                            className="ta-btn"
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 5,
                              padding: "3px 10px",
                              borderRadius: 12,
                              border: "none",
                              background: "#fff",
                              color: c.dot,
                              fontSize: 11,
                              fontWeight: 600,
                              cursor: "pointer",
                            }}
                          >
                            <LabelMarker c={c} size={6} />
                            {c.name}
                          </button>
                        ));
                      })()}
                    </div>
                  )}

                  {committed.length === 0 ? (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "36px 16px",
                        color: "#C0B8AC",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 32,
                          marginBottom: 10,
                          opacity: 0.5,
                          color: "#fff",
                        }}
                      >
                        ✎
                      </div>
                      <p
                        style={{
                          fontSize: 12,
                          lineHeight: 1.6,
                          margin: 0,
                          color: "#fff",
                        }}
                      >
                        Select text, or use "+ Add field" to enter a value
                        manually.
                      </p>
                    </div>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 7,
                      }}
                    >
                      {renderAnnotationList()}
                    </div>
                  )}

                  {committed.length > 0 && (
                    <button
                      onClick={() => {
                        if (window.confirm("Clear all annotations?")) {
                          setAnnotations([]);
                          setSelectedAnnotation(null);
                        }
                      }}
                      className="ta-btn"
                      style={{
                        marginTop: 14,
                        width: "100%",
                        padding: "7px",
                        borderRadius: 8,
                        border: "1px dashed #DDD8D0",
                        background: "transparent",
                        fontSize: 11,
                        color: "#BBB",
                        cursor: "pointer",
                      }}
                    >
                      Clear All
                    </button>
                  )}
                </>
              )}

              {/* ════ GENERAL NOTE TAB ════ */}
              {sidebarTab === "note" && (
                <>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: 18,
                    }}
                  >
                    <h3
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        color: "#fff",
                        margin: 0,
                      }}
                    >
                      General Note
                    </h3>
                    {generalNote.trim() && (
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          background: "#00AD6E",
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </div>

                  <textarea
                    className="ta-general-note-textarea"
                    value={generalNote}
                    onChange={(e) => setGeneralNote(e.target.value)}
                    placeholder="Write your general notes here…"
                    style={{
                      width: "100%",
                      minHeight: 200,
                      padding: 14,
                      fontSize: 13,
                      border: "1px solid rgba(255,255,255,0.15)",
                      borderRadius: 10,
                      resize: "vertical",
                      fontFamily: "'DM Sans',sans-serif",
                      color: "#333",
                      background: "#FDFCFA",
                      outline: "none",
                      lineHeight: 1.7,
                      boxSizing: "border-box",
                    }}
                  />

                  <div
                    style={{
                      marginTop: 10,
                      fontSize: 11,
                      color: "rgba(255,255,255,0.4)",
                      textAlign: "right",
                    }}
                  >
                    {generalNote.length} characters
                  </div>
                </>
              )}

              {/* ════ EVALUATION TAB ════ */}
              {sidebarTab === "evaluation" && (
                <>
                  {/* ── Overall rating ── */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: 14,
                    }}
                  >
                    <h3
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        color: "#fff",
                        margin: 0,
                      }}
                    >
                      Evaluation
                    </h3>
                    {(() => {
                      const rated = EVAL_FIELDS.filter(
                        (f) => evaluation.fields?.[f]?.rating > 0,
                      ).length;
                      return rated > 0 ? (
                        <span
                          style={{
                            fontSize: 11,
                            background: "#FFB81C",
                            color: "#00205B",
                            padding: "2px 8px",
                            borderRadius: 10,
                            fontWeight: 600,
                          }}
                        >
                          {rated}/{EVAL_FIELDS.length} rated
                        </span>
                      ) : null;
                    })()}
                  </div>

                  <div
                    style={{
                      background: "rgba(255,255,255,0.08)",
                      borderRadius: 12,
                      padding: "14px",
                      marginBottom: 16,
                      textAlign: "center",
                      display: "flex",
                      alignItems: "center",
                      flexDirection: "column",
                    }}
                  >
                    <p
                      style={{
                        fontSize: 11,
                        color: "rgba(255,255,255,0.7)",
                        margin: "0 0 8px",
                        fontWeight: 500,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                      }}
                    >
                      Overall Rating
                    </p>
                    <StarRating
                      value={evaluation.overallRating}
                      onChange={(val) => updateEvaluation("overallRating", val)}
                      size={24}
                    />
                    {evaluation.overallRating > 0 && (
                      <p
                        style={{
                          fontSize: 18,
                          color: "#FFB81C",
                          margin: "6px 0 0",
                          fontWeight: 700,
                        }}
                      >
                        {evaluation.overallRating}/5
                      </p>
                    )}
                    <textarea
                      className="ta-general-note-textarea"
                      value={evaluation.evaluationNote || ""}
                      onChange={(e) =>
                        updateEvaluation("evaluationNote", e.target.value)
                      }
                      placeholder="Overall evaluation comment…"
                      style={{
                        width: "100%",
                        minHeight: 60,
                        padding: 10,
                        fontSize: 12,
                        border: "1px solid rgba(255,255,255,0.15)",
                        borderRadius: 8,
                        resize: "vertical",
                        fontFamily: "'DM Sans',sans-serif",
                        color: "#333",
                        background: "#FDFCFA",
                        outline: "none",
                        lineHeight: 1.6,
                        boxSizing: "border-box",
                        marginTop: 10,
                      }}
                    />
                  </div>

                  {/* ── Per-field evaluations ── */}
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      color: "rgba(255,255,255,0.5)",
                      marginBottom: 10,
                    }}
                  >
                    Field-by-field
                  </div>

                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 6 }}
                  >
                    {EVAL_FIELDS.map((field) => {
                      const fieldEval = evaluation.fields?.[field] || {
                        rating: 0,
                        note: "",
                      };
                      const fieldValue = rowData?.[field];
                      const annCount = annotations.filter(
                        (a) => !a.draft && a.label === field,
                      ).length;
                      const isActive = activeAnn?.label === field;
                      const c =
                        annCount > 0
                          ? ANNOTATION_COLORS[labels.indexOf(field)]
                          : null;

                      return (
                        <div
                          key={field}
                          style={{
                            background: isActive
                              ? "rgba(255,184,28,0.18)"
                              : "rgba(255,255,255,0.07)",
                            border: isActive
                              ? "1px solid #FFB81C"
                              : "1px solid rgba(255,255,255,0.1)",
                            borderRadius: 10,
                            padding: "9px 11px",
                            transition: "all 0.2s",
                          }}
                        >
                          {/* Field header */}
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              marginBottom: 5,
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 5,
                              }}
                            >
                              <span
                                style={{
                                  fontSize: 11,
                                  fontWeight: 600,
                                  color: isActive
                                    ? "#FFB81C"
                                    : "rgba(255,255,255,0.85)",
                                }}
                              >
                                {field}
                              </span>
                              {annCount > 0 && (
                                <span
                                  style={{
                                    fontSize: 9,
                                    fontWeight: 700,
                                    background: c?.dot || "#FFB74D",
                                    color: "#fff",
                                    padding: "1px 5px",
                                    borderRadius: 8,
                                  }}
                                >
                                  {annCount} ann
                                </span>
                              )}
                            </div>
                            <StarRating
                              value={fieldEval.rating}
                              onChange={(val) =>
                                updateFieldEvaluation(field, "rating", val)
                              }
                              size={14}
                            />
                          </div>

                          {/* Row value */}
                          {fieldValue ? (
                            <div
                              style={{
                                fontSize: 10,
                                color: "rgba(255,255,255,0.5)",
                                fontStyle: "italic",
                                marginBottom: 5,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              Value:{" "}
                              <span
                                style={{
                                  color: "rgba(255,255,255,0.75)",
                                  fontStyle: "normal",
                                }}
                              >
                                {field === "Connection Type"
                                  ? expandConnectionType(fieldValue)
                                  : fieldValue}
                              </span>
                            </div>
                          ) : (
                            <div
                              style={{
                                fontSize: 10,
                                color: "rgba(255,255,255,0.25)",
                                fontStyle: "italic",
                                marginBottom: 5,
                              }}
                            >
                              No value
                            </div>
                          )}

                          {/* Note — show only when rated or active */}
                          {(fieldEval.rating > 0 || isActive) && (
                            <textarea
                              className="ta-general-note-textarea"
                              value={fieldEval.note}
                              onChange={(e) =>
                                updateFieldEvaluation(
                                  field,
                                  "note",
                                  e.target.value,
                                )
                              }
                              placeholder={`Note on ${field}…`}
                              style={{
                                width: "100%",
                                minHeight: 44,
                                padding: "6px 8px",
                                fontSize: 11,
                                border: "1px solid rgba(255,255,255,0.15)",
                                borderRadius: 6,
                                resize: "vertical",
                                fontFamily: "'DM Sans',sans-serif",
                                color: "#333",
                                background: "#FDFCFA",
                                outline: "none",
                                lineHeight: 1.5,
                                boxSizing: "border-box",
                              }}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}